<template>
  <q-dialog :model-value="modelValue" persistent @update:model-value="$emit('update:modelValue', $event)">
    <q-card class="fact-dialog">

      <!-- Шапка -->
      <div class="fact-dialog__header" :class="{
        'fact-dialog__header--sent': factData.isSent && !stornoMode,
        'fact-dialog__header--storno': stornoMode && factData.isStornoMode,
      }">
        <div class="fact-dialog__title">
          <template v-if="stornoMode && factData.isStornoMode">Оперфакт (режим корректировки / сторно)</template>
          <template v-else-if="factData.isSent">Оперфакт (отправлено в УСП)</template>
          <template v-else>Заполнение Оперфакта</template>
        </div>
        <div class="fact-dialog__subtitle" v-if="factData.date">
          <template v-if="stornoMode && factData.isStornoMode">Корректировка за {{ fmtIsoToRu(factData.date.iso) }}</template>
          <template v-else>{{ factData.isSent ? 'Просмотр данных за' : 'Внести факт за' }} {{ fmtIsoToRu(factData.date.iso) }}</template>
        </div>
        <q-btn icon="close" flat round dense color="white" v-close-popup class="fact-dialog__close" />
      </div>

      <!-- Тело -->
      <div class="fact-dialog__body" style="max-height:65vh;overflow-y:auto">

        <!-- Загрузка -->
        <div v-if="factData.loading" class="fact-dialog__loader">
          <q-spinner color="indigo-7" size="36px" />
        </div>

        <template v-else>

          <!-- Баннер "Режим корректировки" -->
          <div v-if="stornoMode && factData.isStornoMode" class="fact-storno-banner">
            <q-icon name="edit_note" size="18px" />
            <template v-if="factData.isSent">Будет создана корректирующая запись (сторно)</template>
            <template v-else>Факт не отправлен в УСП — изменения будут применены сразу</template>
          </div>

          <!-- Баннер "Отправлено в УСП" -->
          <div v-else-if="factData.isSent" class="fact-sent-banner">
            <q-icon name="check_circle" size="18px" />
            Данные отправлены в УСП. Редактирование недоступно.
          </div>

          <!-- Карточка работы -->
          <div class="fwork-card">
            <div class="fwork-card__name-row">
              <span class="fwork-card__name">{{ factData.row?.label }}</span>
              <q-btn
                v-if="factData.workData"
                icon="content_copy" flat dense size="sm" color="grey-7"
                class="fwork-card__action-btn"
                @click="copyText(factData.workData.work_guid)"
              >
                <q-tooltip>Скопировать guid строки</q-tooltip>
              </q-btn>
            </div>

            <div class="fwork-card__vol-row">
              <span class="fwork-vol__text">
                Общий объём: <b>{{ round6(factData.workData?.plan_total ?? 0) }} {{ factData.workData?.unit }}</b>
              </span>
              <span
                v-if="factData.workData?.is_complete"
                class="fwork-vol__done"
              >
                <q-icon name="check_circle" color="green-7" size="16px" />
                Завершено
              </span>
              <q-btn
                v-else-if="factData.workData"
                label="Завершить"
                flat dense no-caps size="sm"
                color="green-7"
                class="fwork-vol__badge-outline"
                @click="$emit('complete-work', [factData.row])"
              >
                <q-tooltip>Завершить работу</q-tooltip>
              </q-btn>
            </div>

            <div class="fwork-executor" v-if="factData.workData">
              <span class="fwork-executor__label">Факт по исполнителю</span>
              <q-chip
                dense square
                color="primary" text-color="white"
                class="fwork-executor__chip"
              >
                {{ factData.workData.contractor_name || '—' }}
              </q-chip>
              <span class="fwork-executor__val">
                : {{ round6(factData.workData.fact_executor) }} {{ factData.workData.unit }}
              </span>
            </div>
          </div>

          <!-- Секция ввода факта + слайдер -->
          <div class="fact-input-section">
            <div v-if="factData.workData && factData.workData.cumulative_fact > 0" class="fact-cumulative-row">
              <span class="fact-cumulative-row__label">Выполнено ранее:</span>
              <b class="fact-cumulative-row__val">{{ round6(factData.workData.cumulative_fact) }} {{ factData.workData.unit }}</b>
            </div>

            <!-- ═══ Режим сторно: текущие + новые + разница ═══ -->
            <template v-if="stornoMode && factData.isStornoMode">
              <div class="storno-fields">
                <div class="storno-field-row">
                  <div class="storno-field">
                    <span class="storno-field__label">Текущий факт</span>
                    <q-input
                      :model-value="factData.originalFactValue"
                      outlined dense readonly
                      type="number"
                      class="storno-field__input"
                      bg-color="grey-2"
                    />
                  </div>
                  <div class="storno-field">
                    <span class="storno-field__label">Новый факт</span>
                    <q-input
                      :model-value="factData.newFactValue"
                      @update:model-value="(v) => onStornoNewFactInput(v)"
                      outlined dense autofocus
                      type="number"
                      min="0"
                      placeholder="Введите"
                      class="storno-field__input"
                    />
                  </div>
                  <div class="storno-field storno-field--diff">
                    <span class="storno-field__label">Разница</span>
                    <div class="storno-diff-value" :class="{
                      'storno-diff-value--positive': factData.stornoDiff > 0,
                      'storno-diff-value--negative': factData.stornoDiff < 0,
                      'storno-diff-value--zero':     factData.stornoDiff === 0,
                    }">
                      {{ factData.stornoDiff > 0 ? '+' : '' }}{{ round6(factData.stornoDiff) }}
                    </div>
                  </div>
                </div>
                <span class="storno-field__unit" v-if="factData.workData">{{ factData.workData.unit }}</span>
              </div>
            </template>

            <!-- ═══ Обычный режим ═══ -->
            <template v-else>
            <div class="fact-input-row">
              <div class="fact-input-row__left">
                <span class="fact-input-row__label">Факт за {{ factData.date?.label || 'дату' }}:</span>
                <q-input
                  :model-value="factData.factValue"
                  @update:model-value="(v) => onFactValueInput(v)"
                  outlined dense autofocus
                  type="number"
                  :min="factData.minFactValue"
                  :max="factDialogMaxValue"
                  placeholder="Введите"
                  class="fact-input-row__field"
                  :readonly="factData.isSent || factData.workData?.is_complete || !!factData.uspFactId"
                  :bg-color="factData.isSent || factData.workData?.is_complete ? 'green-1' : !!factData.uspFactId ? 'grey-2' : ''"
                  :hint="factData.minFactValue > 0
                    ? `Мин: ${factData.minFactValue} · Макс: ${round6(factDialogMaxValue)}`
                    : `Макс: ${round6(factDialogMaxValue)}`"
                />
                <span class="fact-input-row__unit" v-if="factData.workData">{{ factData.workData.unit }}</span>
              </div>
              <div class="fact-input-row__right" v-if="factData.workData">
                Остаток по работе:
                <b class="fact-input-row__remainder">{{ round6(factData.workData.remainder) }} {{ factData.workData.unit }}</b>
              </div>
            </div>
            </template>

            <!-- KZT-суммы из БД (только если факт уже сохранён) -->
            <div v-if="factData.factPrice != null" class="fact-kzt-block">
              <!-- Работа -->
              <div class="fact-kzt-row">
                <span class="fact-kzt-row__label">Работа:</span>
                <span class="fact-kzt-row__amount">{{ fmtKzt(factData.factPrice) }} ₸</span>
              </div>
              <!-- Материалы (только если есть) -->
              <div v-if="factData.materialsPrice > 0" class="fact-kzt-row fact-kzt-row--mats">
                <span class="fact-kzt-row__label">Материалы:</span>
                <span class="fact-kzt-row__amount">{{ fmtKzt(factData.materialsPrice) }} ₸</span>
              </div>
              <!-- Итого (только если есть материалы) -->
              <div v-if="factData.materialsPrice > 0" class="fact-kzt-row fact-kzt-row--total">
                <span class="fact-kzt-row__label">Итого:</span>
                <span class="fact-kzt-row__amount fact-kzt-row__amount--total">{{ fmtKzt(factDialogKztTotal) }} ₸</span>
              </div>
            </div>

            <!-- Полоса факта — Opera Build -->
            <div
              class="fwork-bar"
              ref="fworkBar"
              @mousedown="!factData.isSent && !factData.workData?.is_complete && !factData.uspFactId && onBarMouseDown($event)"
              @touchstart.prevent="!factData.isSent && !factData.workData?.is_complete && !factData.uspFactId && onBarTouchStart($event)"
            >
              <div
                class="fwork-bar__fill"
                :style="{ width: Math.max(factData.factPct, 0) + '%' }"
              />
              <q-icon
                v-if="factData.workData?.cumulative_fact > 0"
                class="fwork-bar__lock"
                name="lock"
                size="12px"
                color="white"
                :style="{
                  left: Math.min(96, Math.round(
                    (factData.workData.cumulative_fact / (factData.workData.plan_total || 1)) * 100
                  )) + '%'
                }"
              />
              <div
                class="fwork-bar__pct-pill"
                :style="factData.factPct >= 85
                  ? { right: '4px' }
                  : { left: 'calc(' + factData.factPct + '% + 4px)' }"
              >
                <q-icon
                  v-if="factData.factPct >= 100 && factData.minFactValue > 0"
                  name="lock"
                  size="12px"
                  color="white"
                  style="margin-right: 3px; vertical-align: middle;"
                />{{ factData.factPct }}&nbsp;%
              </div>
            </div>
          </div>

          <!-- Материалы -->
          <div class="fact-materials">
            <div class="fact-materials__header">
              <span class="fact-materials__title">Материалы</span>
            </div>

            <div v-if="factData.materialsOpen && factData.workData?.materials?.length" class="fact-materials__grid">
              <div
                v-for="(mat, idx) in factData.workData.materials"
                v-show="mat.isShow !== false"
                :key="idx"
                class="fact-mat-item"
                :class="{
                  'fact-mat-item--replaced': mat.isReplaced,
                  'fact-mat-item--manual':   mat.isManual && !mat.isReplaced,
                }"
              >
                <div class="fact-mat-item__name-row">
                  <span class="fact-mat-item__name">{{ visibleMatIndex(factData.workData.materials, idx) }}. {{ mat.name }}</span>
                  <q-badge
                    v-if="mat.isManual && !mat.isReplaced"
                    color="deep-orange-5" outline label="вручную"
                    class="fact-mat-item__badge"
                  />
                </div>

                <!-- ═══ Материалы: режим сторно ═══ -->
                <template v-if="stornoMode && factData.isStornoMode">
                  <div class="storno-mat-row">
                    <div class="storno-mat-col">
                      <span class="storno-mat-col__label">Текущее</span>
                      <q-input
                        :model-value="getOriginalMatValue(mat)"
                        outlined dense readonly type="number"
                        class="storno-mat-col__input"
                        bg-color="grey-2"
                      />
                    </div>
                    <div class="storno-mat-col">
                      <span class="storno-mat-col__label">Новое</span>
                      <q-input
                        :model-value="getStornoMatNewValue(mat)"
                        @update:model-value="(v) => onStornoMatInput(mat, v)"
                        outlined dense type="number" min="0"
                        class="storno-mat-col__input"
                      />
                    </div>
                    <div class="storno-mat-col storno-mat-col--diff">
                      <span class="storno-mat-col__label">Разница</span>
                      <div class="storno-diff-value storno-diff-value--sm" :class="{
                        'storno-diff-value--positive': getStornoMatDiff(mat) > 0,
                        'storno-diff-value--negative': getStornoMatDiff(mat) < 0,
                        'storno-diff-value--zero':     getStornoMatDiff(mat) === 0,
                      }">
                        {{ getStornoMatDiff(mat) > 0 ? '+' : '' }}{{ round6(getStornoMatDiff(mat)) }}
                      </div>
                    </div>
                    <span class="storno-mat-unit">{{ mat.unit }}</span>
                  </div>
                </template>

                <!-- ═══ Материалы: обычный режим ═══ -->
                <template v-else>
                <div class="fact-mat-item__input-row">
                  <q-input
                    :model-value="mat.value"
                    @update:model-value="(v) => onMaterialInput(mat, v)"
                    outlined dense type="number" min="0"
                    :max="materialZhcpMax(mat)"
                    class="fact-mat-item__input"
                    :readonly="!mat.isEdit || factData.isSent || factData.workData?.is_complete || !!factData.uspFactId"
                    :bg-color="factData.isSent || factData.workData?.is_complete ? 'green-1' : !mat.isEdit || !!factData.uspFactId ? 'grey-2' : mat.isManual ? 'orange-1' : ''"
                  />
                  <q-btn
                    v-if="mat.isEdit && !factData.isSent && !factData.workData?.is_complete && !factData.uspFactId && mat.isManual"
                    icon="refresh" flat round dense size="xs"
                    color="deep-orange-5"
                    @click="resetMaterial(mat)"
                  >
                    <q-tooltip>Вернуть авторасчёт</q-tooltip>
                  </q-btn>
                </div>
                <div v-if="!mat.isReplaced" class="fact-mat-item__info">
                  Норма расхода: <b>{{ mat.norm }}</b>
                  <span v-if="mat.norm > 0" class="fact-mat-item__formula">
                    = {{ factData.factValue || 0 }} × {{ mat.norm }}
                  </span>
                </div>
                <div v-if="!mat.isReplaced" class="fact-mat-item__info">
                  Использовано: <b>{{ round6(mat.use_amount) }}</b> {{ mat.unit }}
                  &nbsp;·&nbsp;
                  Остаток по ЖЦП: <b>{{ round6(mat.remainder) }}</b> {{ mat.unit }}
                </div>
                </template>
              </div>
            </div>

            <div v-if="factData.materialsOpen && !factData.workData?.materials?.length" class="fact-materials__empty">
              Нет материалов
            </div>
          </div>

        </template>
      </div>

      <!-- История корректировок -->
      <div v-if="stornoMode && factData.isStornoMode && (factData.correctionHistory?.length || factData.correctionHistoryLoading)" class="storno-history">
        <div class="storno-history__title">
          <q-icon name="history" size="16px" />
          История корректировок
          <q-spinner v-if="factData.correctionHistoryLoading" size="14px" color="grey-6" class="q-ml-xs" />
        </div>
        <div v-if="factData.correctionHistory?.length" class="storno-history__list">
          <div v-for="(h, hi) in factData.correctionHistory" :key="hi" class="storno-history__item">
            <div class="storno-history__date">{{ fmtDateTime(h.rowversion) }}</div>
            <div class="storno-history__values">
              <span class="storno-history__val">{{ round6(h.original_fact_value) }}</span>
              <q-icon name="arrow_forward" size="12px" color="grey-5" />
              <span class="storno-history__val">{{ round6(h.new_fact_value) }}</span>
              <span class="storno-history__diff" :class="{
                'storno-history__diff--pos': h.diff_fact_value > 0,
                'storno-history__diff--neg': h.diff_fact_value < 0,
              }">
                ({{ h.diff_fact_value > 0 ? '+' : '' }}{{ round6(h.diff_fact_value) }})
              </span>
            </div>
          </div>
        </div>
      </div>

      <!-- Футер -->
      <div class="fact-dialog__footer">
        <!-- Режим сторно -->
        <template v-if="stornoMode && factData.isStornoMode">
          <q-btn
            :label="factData.isSent ? 'Сохранить корректировку' : 'Применить изменения'"
            :color="factData.isSent ? 'deep-orange' : 'primary'"
            unelevated no-caps
            class="fact-dialog__save-btn"
            :loading="factData.saving"
            @click="$emit('save-storno')"
          />
          <q-btn label="Отменить" flat no-caps color="grey-7" v-close-popup />
        </template>
        <template v-else-if="factData.isSent || factData.workData?.is_complete">
          <q-btn label="Закрыть" flat no-caps color="grey-7" v-close-popup />
        </template>
        <template v-else-if="factData.uspFactId">
          <span style="font-size: 12px; color: #888; margin-right: auto;">
            Для изменения — удалите и введите заново
          </span>
          <q-btn
            label="Удалить"
            unelevated no-caps
            color="negative"
            :loading="factData.deleting"
            @click="$emit('delete')"
          />
          <q-btn label="Закрыть" flat no-caps color="grey-7" v-close-popup />
        </template>
        <template v-else>
          <q-btn
            label="Сохранить"
            color="primary" unelevated no-caps
            class="fact-dialog__save-btn"
            :loading="factData.saving"
            @click="$emit('save')"
          />
          <q-btn label="Отменить" flat no-caps color="grey-7" v-close-popup />
        </template>
      </div>

    </q-card>
  </q-dialog>
</template>

<script>
import { useFormatters } from '../composables/useFormatters';
import {
  computeMaxFactValue,
  applyNewFactInput,
  applyMaterialInput,
  getMaterialDiff,
  getMaterialNewValue,
  getOriginalMaterialValue,
  recalcStornoMaterialsByFact,
} from '../composables/useStornoEditing';

export default {
  name: 'PpmFactDialog',
  props: {
    modelValue: Boolean,
    factData: { type: Object, required: true },
    factDialogKztTotal: { type: Number, default: 0 },
    stornoMode: { type: Boolean, default: false },
    exceedAllowed: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'save', 'save-storno', 'delete', 'complete-work', 'uncomplete-work'],
  setup() {
    const { fmtIsoToRu, fmtKzt, round6, r6, parseFactNum } = useFormatters();
    return { fmtIsoToRu, fmtKzt, round6, r6, parseFactNum };
  },
  computed: {
    factDialogMaxValue() {
      const plan       = this.parseFactNum(this.factData.workData?.plan_total);
      const cumulative = this.parseFactNum(this.factData.workData?.cumulative_fact);
      return Math.max(0, this.r6(plan - cumulative));
    },
  },
  methods: {
    fmtDateTime(iso) {
      if (!iso) return '—';
      try {
        const d = new Date(iso);
        return d.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
      } catch { return iso; }
    },
    async copyText(text) {
      try {
        await navigator.clipboard.writeText(text || '');
      } catch { /* ignore */ }
    },

    visibleMatIndex(materials, idx) {
      let n = 0;
      for (let i = 0; i < idx; i++) {
        if (materials[i].isShow !== false) n++;
      }
      return n + 1;
    },

    _recalcMaterials(factNum) {
      const mats = this.factData.workData?.materials;
      if (!mats) return;
      const workPlan = this.parseFactNum(this.factData.workData?.plan_total);
      mats.forEach((m) => {
        if (!m.isManual) {
          const auto = workPlan > 0
            ? this.r6(m.plan_amount * (factNum / workPlan))
            : this.r6(factNum * m.norm);
          m.value = this.clampMaterialToZhcpRemainder(m, auto, false);
        }
      });
    },

    onFactPctChange(pct) {
      if (this.factData.isSent) return;
      const plan       = this.factData.workData?.plan_total || 0;
      const cumulative = this.factData.workData?.cumulative_fact || 0;
      const minVal     = this.factData.minFactValue || 0;
      const maxVal     = this.factDialogMaxValue;
      if (plan > 0) {
        const totalForPct = this.r6((pct / 100) * plan);
        const newVal      = Math.min(maxVal, Math.max(minVal, this.r6(totalForPct - cumulative)));
        this.factData.factValue = String(newVal);
        this.factData.factPct   = Math.min(100, Math.round(((cumulative + newVal) / plan) * 100));
        if (this.factData.workData) {
          this.factData.workData.remainder = Math.max(0, this.r6(plan - cumulative - newVal));
        }
        this._recalcMaterials(newVal);
      }
    },

    _barPctFromEvent(e) {
      const bar = this.$refs.fworkBar;
      if (!bar) return null;
      const rect = bar.getBoundingClientRect();
      const x = (e.touches ? e.touches[0].clientX : e.clientX) - rect.left;
      return Math.max(0, Math.min(100, Math.round((x / rect.width) * 100)));
    },

    onBarMouseDown(e) {
      if (this.factData.isSent) return;
      const pct = this._barPctFromEvent(e);
      if (pct !== null) { this.onFactPctChange(pct); }
      const onMove = (ev) => {
        const p = this._barPctFromEvent(ev);
        if (p !== null) { this.onFactPctChange(p); }
      };
      const onUp = () => { document.removeEventListener('mousemove', onMove); document.removeEventListener('mouseup', onUp); };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },

    onBarTouchStart(e) {
      if (this.factData.isSent) return;
      const pct = this._barPctFromEvent(e);
      if (pct !== null) { this.onFactPctChange(pct); }
      const onMove = (ev) => {
        const p = this._barPctFromEvent(ev);
        if (p !== null) { this.onFactPctChange(p); }
      };
      const onEnd = () => { document.removeEventListener('touchmove', onMove); document.removeEventListener('touchend', onEnd); };
      document.addEventListener('touchmove', onMove);
      document.addEventListener('touchend', onEnd);
    },

    onFactValueInput(val) {
      if (this.factData.isSent) return;
      const minVal = this.factData.minFactValue || 0;
      const maxVal = this.factDialogMaxValue;
      let num      = this.parseFactNum(val);
      if (num < minVal) num = minVal;
      if (num > maxVal) num = maxVal;
      num = this.r6(num);
      this.factData.factValue = String(num);
      const plan       = this.factData.workData?.plan_total || 0;
      const cumulative = this.factData.workData?.cumulative_fact || 0;
      this.factData.factPct = plan > 0
        ? Math.min(100, Math.round(((cumulative + num) / plan) * 100))
        : 0;
      if (this.factData.workData) {
        this.factData.workData.remainder = Math.max(0, this.r6(plan - cumulative - num));
      }
      this._recalcMaterials(num);
    },

    materialZhcpMax(mat) {
      if (mat?.remainder == null || mat.remainder === '') return undefined;
      return Math.max(0, this.parseFactNum(mat.remainder));
    },

    clampMaterialToZhcpRemainder(mat, rawVal, notifyIfClamped) {
      let num = this.parseFactNum(rawVal);
      if (num < 0) num = 0;
      if (mat?.remainder == null || mat.remainder === '') return String(this.r6(num));
      const rem = this.parseFactNum(mat.remainder);
      const cap = this.r6(Math.max(0, rem));
      if (num > cap + 1e-6) {
        if (notifyIfClamped) {
          this.$q?.notify?.({
            type:    'warning',
            message: `Не больше остатка по ЖЦП: ${this.round6(cap)} ${mat.unit || ''}`.trim(),
          });
        }
        return String(cap);
      }
      return String(this.r6(num));
    },

    materialsExceedingZhcpRemainder(materials) {
      const out = [];
      for (const m of materials || []) {
        const v = this.parseFactNum(m.value);
        if (m?.remainder == null || m.remainder === '') continue;
        const rem = this.parseFactNum(m.remainder);
        const cap = Math.max(0, rem);
        if (v > cap + 1e-6) {
          out.push({ name: m.name, cap, unit: m.unit || '', value: v });
        }
      }
      return out;
    },

    onMaterialInput(mat, val) {
      if (this.factData.isSent) return;
      mat.value    = this.clampMaterialToZhcpRemainder(mat, val, true);
      mat.isManual = true;

      if (!this.factData.matSyncFact) return;

      const num  = this.parseFactNum(mat.value);
      const norm = mat.norm || 0;
      if (norm > 0) {
        const newFact = this.r6(num / norm);
        this.factData.factValue = String(newFact);

        const plan       = this.factData.workData?.plan_total || 0;
        const cumulative = this.factData.workData?.cumulative_fact || 0;
        this.factData.factPct = plan > 0
          ? Math.min(100, Math.round(((cumulative + newFact) / plan) * 100))
          : 0;
        if (this.factData.workData) {
          this.factData.workData.remainder = Math.max(0, this.r6(plan - cumulative - newFact));
        }

        const mats     = this.factData.workData?.materials || [];
        const workPlan = this.parseFactNum(this.factData.workData?.plan_total);
        mats.forEach((m) => {
          if (m.isManual) return;
          const raw = workPlan > 0 ? this.r6(m.plan_amount * (newFact / workPlan)) : this.r6(newFact * m.norm);
          m.value = this.clampMaterialToZhcpRemainder(m, raw, false);
        });
      }
    },

    resetMaterial(mat) {
      const num      = this.parseFactNum(this.factData.factValue);
      const workPlan = this.parseFactNum(this.factData.workData?.plan_total);
      const raw      = workPlan > 0 ? this.r6(mat.plan_amount * (num / workPlan)) : this.r6(num * mat.norm);
      mat.value              = this.clampMaterialToZhcpRemainder(mat, raw, false);
      mat.isManual           = false;
      mat.isReplaced         = false;
      mat.replacedMaterialId = null;
    },

    resetAllMaterials() {
      const num      = this.parseFactNum(this.factData.factValue);
      const workPlan = this.parseFactNum(this.factData.workData?.plan_total);
      (this.factData.workData?.materials || []).forEach((m) => {
        const raw = workPlan > 0 ? this.r6(m.plan_amount * (num / workPlan)) : this.r6(num * m.norm);
        m.value              = this.clampMaterialToZhcpRemainder(m, raw, false);
        m.isManual           = false;
        m.isReplaced         = false;
        m.replacedMaterialId = null;
      });
    },

    stornoMaxFactValue() {
      return computeMaxFactValue({
        plan:          this.factData.workData?.plan_total,
        cumulative:    this.factData.workData?.cumulative_fact,
        exceedAllowed: this.exceedAllowed,
        r6:            this.r6,
      });
    },

    onStornoNewFactInput(val) {
      const { newFactValue, stornoDiff } = applyNewFactInput({
        val,
        originalValue: this.factData.originalFactValue,
        maxVal:        this.stornoMaxFactValue(),
        parseFactNum:  this.parseFactNum,
        r6:            this.r6,
        notify:        (o) => this.$q?.notify?.(o),
      });
      this.factData.newFactValue = newFactValue;
      this.factData.stornoDiff   = stornoDiff;
      recalcStornoMaterialsByFact({
        newFactValue,
        originalMaterials: this.factData.originalMaterials,
        workMaterials:     this.factData.workData?.materials,
        workPlan:          this.factData.workData?.plan_total,
        parseFactNum:      this.parseFactNum,
        r6:                this.r6,
      });
    },

    getOriginalMatValue(mat) {
      return getOriginalMaterialValue(this.factData.originalMaterials, mat);
    },

    getStornoMatNewValue(mat) {
      return getMaterialNewValue(this.factData.originalMaterials, mat);
    },

    getStornoMatDiff(mat) {
      return getMaterialDiff({
        originalMaterials: this.factData.originalMaterials,
        mat,
        parseFactNum:      this.parseFactNum,
        r6:                this.r6,
      });
    },

    onStornoMatInput(mat, val) {
      applyMaterialInput({
        val,
        mat,
        originalMaterials: this.factData.originalMaterials,
        parseFactNum:      this.parseFactNum,
        r6:                this.r6,
        notify:            (o) => this.$q?.notify?.(o),
      });
    },
  },
};
</script>

<style scoped>
.storno-history {
  border-top: 1px solid #e0e0e0;
  padding: 8px 16px;
  max-height: 160px;
  overflow-y: auto;
  background: #fafafa;
}
.storno-history__title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  font-weight: 600;
  color: #546e7a;
  margin-bottom: 6px;
}
.storno-history__list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}
.storno-history__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 8px;
  background: #fff;
  border-radius: 4px;
  border: 1px solid #eceff1;
  font-size: 12px;
}
.storno-history__date {
  color: #90a4ae;
  font-size: 11px;
  white-space: nowrap;
}
.storno-history__values {
  display: flex;
  align-items: center;
  gap: 6px;
}
.storno-history__val {
  font-weight: 500;
  color: #37474f;
}
.storno-history__diff {
  font-weight: 600;
  font-size: 11px;
}
.storno-history__diff--pos {
  color: #2e7d32;
}
.storno-history__diff--neg {
  color: #c62828;
}
</style>
